import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Ticket, Plus, Loader2, Printer, Search, Filter, ChevronLeft, ChevronRight, FileDown, Trash2, MoreVertical } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import Tooltip from '../components/ui/Tooltip';
import DatePicker from '../components/ui/DatePicker';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import { formatDate } from '../utils/formatDate';
import { API_PATH, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import styles from './CouponPage.module.css';

const PLANS = [
    { id: 'silver', label: 'Silver' },
    { id: 'gold', label: 'Gold' },
    { id: 'platinum', label: 'Platinum' },
];

const PLAN_COLORS = {
    silver: {
        border: '#64748b',
        accent: '#475569',
        bg: '#f1f5f9',
        text: '#334155',
    },
    gold: {
        border: '#a16207',
        accent: '#ca8a04',
        bg: '#fefce8',
        text: '#713f12',
    },
    platinum: {
        border: '#5b21b6',
        accent: '#7c3aed',
        bg: '#f5f3ff',
        text: '#4c1d95',
    },
};


function getDefaultDateRange() {
    const to = new Date();
    const from = new Date(to);
    from.setMonth(from.getMonth() - 1);
    return {
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
    };
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SORT_OPTIONS = [
    { value: 'created_at_desc', sortBy: 'created_at', sortOrder: 'desc', label: 'Created (newest first)' },
    { value: 'created_at_asc', sortBy: 'created_at', sortOrder: 'asc', label: 'Created (oldest first)' },
    { value: 'code_asc', sortBy: 'code', sortOrder: 'asc', label: 'Code (A–Z)' },
    { value: 'plan_asc', sortBy: 'plan', sortOrder: 'asc', label: 'Plan (A–Z)' },
    { value: 'status_asc', sortBy: 'status', sortOrder: 'asc', label: 'Status' },
];
const PLAN_OPTIONS = [
    { value: '', label: 'All plans' },
    ...PLANS.map((p) => ({ value: p.id, label: p.label })),
];
const STATUS_OPTIONS = [
    { value: '', label: 'All status' },
    { value: 'active', label: 'Active' },
    { value: 'redeemed', label: 'Redeemed' },
];

const BRAND_NAME = 'LivePulse';

const getCouponValueText = (coupon) =>
    coupon.value_type === 'coins'
        ? `${coupon.coin_value} Coins`
        : coupon.subscription_days
            ? `${coupon.subscription_days} days subscription`
            : `Subscription till ${formatDate(coupon.subscription_end_date) || '—'}`;

const getCouponHtml = (coupon, options = {}) => {
    const valueText = getCouponValueText(coupon);
    const baseUrl = (options.baseUrl || '').replace(/\/$/, '');
    const qrData = baseUrl ? `${baseUrl}/credits?code=${encodeURIComponent(coupon.code)}` : coupon.code;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(qrData)}`;
    const printScript = options.forPrint
        ? '<script>window.onload = function() { window.print(); window.close(); }</script>'
        : '';
    const planKey = (coupon.plan || 'silver').toLowerCase();
    const colors = PLAN_COLORS[planKey] || PLAN_COLORS.silver;
    return `<!DOCTYPE html>
<html>
<head>
  <title>Coupon - ${coupon.code}</title>
  <style>
    body { font-family: system-ui, sans-serif; padding: 24px; max-width: 360px; margin: 0 auto; background: ${colors.bg}; }
    .coupon { border: 2px dashed ${colors.border}; border-radius: 12px; padding: 24px; text-align: center; background: #fff; }
    .brand { font-size: 1.35rem; font-weight: 800; letter-spacing: 0.08em; color: ${colors.accent}; margin-bottom: 4px; }
    .brandSub { font-size: 0.65rem; letter-spacing: 0.2em; color: ${colors.text}; text-transform: uppercase; margin-bottom: 16px; }
    .code { font-size: 1.5rem; font-weight: 700; letter-spacing: 0.2em; margin: 16px 0; color: ${colors.text}; }
    .plan { text-transform: uppercase; font-size: 0.75rem; font-weight: 700; color: ${colors.accent}; }
    .amount { font-size: 1.25rem; margin: 8px 0; color: ${colors.text}; }
    .value { font-size: 1rem; color: ${colors.accent}; font-weight: 600; }
    .qrWrap { margin: 16px 0; }
    .qrWrap img { display: block; margin: 0 auto; width: 120px; height: 120px; }
    .footer { margin-top: 16px; font-size: 0.75rem; color: #64748b; }
    @media print { body { padding: 0; background: #fff; } .coupon { border-color: ${colors.border}; } }
  </style>
</head>
<body>
  <div class="coupon">
    <div class="brand">${BRAND_NAME}</div>
    <div class="brandSub">Rewards &amp; Credits</div>
    <div class="plan">${coupon.plan} Plan</div>
    <div class="amount">Amount: ${coupon.amount}</div>
    <div class="value">${valueText}</div>
    <div class="code">${coupon.code}</div>
    <div class="qrWrap"><img src="${qrUrl}" alt="QR code: ${coupon.code}" width="120" height="120" /></div>
    <div class="footer">Scan or enter code in Settings to credit your account. One-time use.</div>
  </div>
  ${printScript}
</body>
</html>`;
};

const printCoupon = (coupon, baseUrl) => {
    const win = window.open('', '_blank', 'width=400,height=560');
    if (!win) return;
    win.document.write(getCouponHtml(coupon, { forPrint: true, baseUrl: baseUrl || window.location.origin }));
    win.document.close();
};

const exportCouponPdf = (coupon, baseUrl) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('style', 'position:fixed;left:-9999px;width:400px;height:560px;border:none;');
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(getCouponHtml(coupon, { forPrint: false, baseUrl: baseUrl || window.location.origin }));
    doc.close();

    const waitForPdf = () => {
        const body = doc.body;
        if (!body) {
            requestAnimationFrame(waitForPdf);
            return;
        }
        const img = body.querySelector('.qrWrap img');
        const onReady = () => {
            html2canvas(body, {
                useCORS: true,
                allowTaint: true,
                scale: 2,
                logging: false,
                backgroundColor: '#ffffff',
            }).then((canvas) => {
                try {
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const imgData = canvas.toDataURL('image/png');
                    const pdfW = 210;
                    const pdfH = 297;
                    const margin = 10;
                    const maxW = pdfW - 2 * margin;
                    const maxH = pdfH - 2 * margin;
                    const aspect = canvas.width / canvas.height;
                    const w = maxW / maxH > aspect ? maxH * aspect : maxW;
                    const h = maxW / maxH > aspect ? maxH : maxW / aspect;
                    pdf.addImage(imgData, 'PNG', (pdfW - w) / 2, (pdfH - h) / 2, w, h);
                    pdf.save(`coupon-${coupon.code}.pdf`);
                } finally {
                    document.body.removeChild(iframe);
                }
            }).catch(() => {
                document.body.removeChild(iframe);
            });
        };
        if (img && !img.complete) {
            img.onload = onReady;
            img.onerror = onReady;
            setTimeout(onReady, 3000);
        } else {
            setTimeout(onReady, 300);
        }
    };
    iframe.onload = waitForPdf;
};

const defaultRange = getDefaultDateRange();

const CouponPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [coupons, setCoupons] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showGenerate, setShowGenerate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');
    const [plan, setPlan] = useState('silver');
    const [amount, setAmount] = useState('');
    const [coinValue, setCoinValue] = useState('');
    const [subscriptionDays, setSubscriptionDays] = useState('');
    const [createdCoupon, setCreatedCoupon] = useState(null);

    const [search, setSearch] = useState('');
    const [from, setFrom] = useState(defaultRange.from);
    const [to, setTo] = useState(defaultRange.to);
    const [planFilter, setPlanFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortValue, setSortValue] = useState('created_at_desc');
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(1);

    const [deleteModal, setDeleteModal] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    const [openMenuId, setOpenMenuId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);

    const isSuperadmin = user?.role === 'superadmin';

    useLayoutEffect(() => {
        if (!openMenuId || !triggerRef.current) {
            setDropdownPosition(null);
            return;
        }
        const rect = triggerRef.current.getBoundingClientRect();
        const dropdownWidth = 180;
        setDropdownPosition({
            top: rect.bottom + 4,
            left: Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 8),
        });
    }, [openMenuId]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpenMenuId(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchCoupons = useCallback(async () => {
        if (!isSuperadmin) return;
        setLoading(true);
        setError(null);
        try {
            const sortOpt = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
                from,
                to,
                sortBy: sortOpt.sortBy,
                sortOrder: sortOpt.sortOrder,
            });
            if (search.trim()) params.set('search', search.trim());
            if (planFilter) params.set('plan', planFilter);
            if (statusFilter) params.set('status', statusFilter);
            const res = await request(`${API_PATH}/superadmin/coupons?${params.toString()}`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || 'Failed to load coupons');
                setCoupons([]);
                return;
            }
            setCoupons(data.coupons || []);
            setPagination(data.pagination || { page: 1, pageSize: 20, total: 0, totalPages: 1 });
        } catch (e) {
            setError('Network error');
            setCoupons([]);
        } finally {
            setLoading(false);
        }
    }, [isSuperadmin, page, pageSize, from, to, search, planFilter, statusFilter, sortValue]);

    useEffect(() => {
        if (!isSuperadmin) {
            navigate('/dashboard', { replace: true });
            return;
        }
        fetchCoupons();
    }, [isSuperadmin, navigate, fetchCoupons]);

    const resetGenerateForm = () => {
        setPlan('silver');
        setAmount('');
        setCoinValue('');
        setSubscriptionDays('');
        setCreateError('');
        setCreatedCoupon(null);
    };

    const openGenerate = () => {
        resetGenerateForm();
        setShowGenerate(true);
    };

    const closeGenerate = () => {
        setShowGenerate(false);
        setCreating(false);
        resetGenerateForm();
        if (createdCoupon) fetchCoupons();
    };

    const handleCreate = async () => {
        const amt = (amount || '').trim();
        if (!amt) {
            setCreateError('Amount (display label) is required');
            return;
        }
        if (plan === 'platinum') {
            const days = parseInt(subscriptionDays, 10);
            if (!Number.isInteger(days) || days < 1) {
                setCreateError('Enter number of days (1 or more) for Platinum');
                return;
            }
        } else {
            const coins = parseInt(coinValue, 10);
            if (!Number.isInteger(coins) || coins < 1) {
                setCreateError('Enter a valid number of coins (1 or more)');
                return;
            }
        }
        setCreating(true);
        setCreateError('');
        try {
            const body =
                plan === 'platinum'
                    ? { plan, amount: amt, subscription_days: parseInt(subscriptionDays, 10) }
                    : { plan, amount: amt, coin_value: parseInt(coinValue, 10) };
            const res = await request(`${API_PATH}/superadmin/coupons`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setCreatedCoupon(data.coupon);
            } else {
                setCreateError(data.error || 'Failed to create coupon');
            }
        } catch (e) {
            setCreateError('Network error');
        } finally {
            setCreating(false);
        }
    };

    const handlePrint = (coupon) => {
        printCoupon(coupon, typeof window !== 'undefined' ? window.location.origin : '');
    };

    const handleExportPdf = (coupon) => {
        exportCouponPdf(coupon, typeof window !== 'undefined' ? window.location.origin : '');
    };

    const openDeleteModal = (coupon) => {
        if (coupon.status !== 'active') return;
        setDeleteModal(coupon);
        setDeleteError('');
    };

    const closeDeleteModal = () => {
        setDeleteModal(null);
        setDeleteError('');
    };

    const handleDeleteCoupon = async () => {
        if (!deleteModal) return;
        setDeletingId(deleteModal.id);
        setDeleteError('');
        try {
            const res = await request(`${API_PATH}/superadmin/coupons/${deleteModal.id}`, { method: 'DELETE' });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                closeDeleteModal();
                await fetchCoupons();
            } else {
                setDeleteError(data.error || 'Failed to delete coupon');
            }
        } catch (e) {
            setDeleteError('Network error');
        } finally {
            setDeletingId(null);
        }
    };

    if (!isSuperadmin) return null;

    const columns = [
        { key: 'code', header: 'Code', render: (row) => <code className={styles.codeCell}>{row.code}</code> },
        {
            key: 'plan',
            header: 'Plan',
            render: (row) => {
                const planKey = (row.plan || 'silver').toLowerCase();
                const planStyle = planKey in PLAN_COLORS ? {
                    background: PLAN_COLORS[planKey].bg,
                    color: PLAN_COLORS[planKey].text,
                    border: `1px solid ${PLAN_COLORS[planKey].border}`,
                } : {};
                return (
                    <span className={styles.planBadge} style={planStyle}>
                        {row.plan}
                    </span>
                );
            },
        },
        { key: 'amount', header: 'Amount' },
        {
            key: 'value',
            header: 'Value',
            render: (row) =>
                row.value_type === 'coins' ? (
                    <span>{row.coin_value} coins</span>
                ) : row.subscription_days ? (
                    <span>{row.subscription_days} days</span>
                ) : (
                    <span>Till {formatDate(row.subscription_end_date) || '—'}</span>
                ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (row) => (
                <Badge variant={row.status === 'active' ? 'success' : 'neutral'} size="sm">
                    {row.status === 'active' ? 'Active' : 'Redeemed'}
                </Badge>
            ),
        },
        { key: 'created_at', header: 'Created', render: (row) => formatDate(row.created_at) },
        {
            key: 'redeemed',
            header: 'Redeemed by',
            render: (row) => {
                const name = row.redeemed_by_name || '—';
                const email = row.redeemed_by_email;
                if (!name || name === '—') return <span>—</span>;
                return (
                    <Tooltip title={email ? `Email: ${email}` : undefined} placement="top">
                        <span className={styles.redeemedByCell}>{name}</span>
                    </Tooltip>
                );
            },
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
                <div className={styles.rowActionsWrap}>
                    <button
                        ref={openMenuId === row.id ? triggerRef : null}
                        type="button"
                        className={styles.menuTrigger}
                        onClick={() => setOpenMenuId((id) => (id === row.id ? null : row.id))}
                        aria-haspopup="true"
                        aria-expanded={openMenuId === row.id}
                        aria-label="Actions"
                    >
                        <MoreVertical size={18} />
                    </button>
                </div>
            ),
        },
    ];

    const totalPages = pagination.totalPages || 1;
    const total = pagination.total || 0;

    return (
        <div className={styles.page}>
            <PageHeroHeader
                icon={Ticket}
                title="Coupons"
                subtitle="Generate plan-wise coupons. Admins redeem them for coins or subscription. Default: last 1 month."
                actions={<Button variant="primary" onClick={openGenerate} icon={<Plus size={18} />} className={styles.pillCta}>Generate coupon</Button>}
            />

            <div className={styles.filters} role="region" aria-label="Filters">
                <div className={styles.filterRow}>
                    <label className={styles.filterLabel}>
                        <Search size={14} aria-hidden /> Search
                    </label>
                    <input
                        type="search"
                        className={styles.searchInput}
                        placeholder="Code or name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
                        aria-label="Search coupons"
                    />
                </div>
                <div className={styles.filterRow}>
                    <label className={styles.filterLabel}>
                        <Filter size={14} aria-hidden /> Date range
                    </label>
                    <DatePicker value={from} onChange={setFrom} max={to} />
                    <span className={styles.filterTo}>to</span>
                    <DatePicker value={to} onChange={setTo} min={from} />
                </div>
                <div className={styles.filterRow}>
                    <label className={styles.filterLabel}>Plan</label>
                    <select className={styles.select} value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} aria-label="Plan">
                        {PLAN_OPTIONS.map((opt) => (
                            <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterRow}>
                    <label className={styles.filterLabel}>Status</label>
                    <select className={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
                        {STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterRow}>
                    <label className={styles.filterLabel}>Sort</label>
                    <select className={styles.select} value={sortValue} onChange={(e) => setSortValue(e.target.value)} aria-label="Sort">
                        {SORT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterRow}>
                    <label className={styles.filterLabel}>Per page</label>
                    <select className={styles.select} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} aria-label="Per page">
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>
                <Button variant="primary" size="sm" onClick={() => { setPage(1); fetchCoupons(); }} disabled={loading}>
                    Apply
                </Button>
            </div>

            {error && (
                <div className={styles.errorBanner} role="alert">
                    {error}
                </div>
            )}

            {loading ? (
                <div className={styles.loading}>
                    <Loader2 size={32} className={styles.spinner} />
                    Loading coupons…
                </div>
            ) : (
                <>
                    <div className={styles.tableWrap}>
                        <Table columns={columns} rows={coupons} emptyMessage="No coupons in this date range. Try different filters or generate one." />
                    </div>
                    {total > 0 && (
                        <div className={styles.pagination} role="navigation" aria-label="Pagination">
                            <span className={styles.paginationSummary}>
                                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                            </span>
                            <div className={styles.paginationButtons}>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1 || loading}
                                    icon={<ChevronLeft size={16} />}
                                    aria-label="Previous page"
                                >
                                    Previous
                                </Button>
                                <span className={styles.pageIndicator}>Page {page} of {totalPages}</span>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages || loading}
                                    iconRight={<ChevronRight size={16} />}
                                    aria-label="Next page"
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {openMenuId && dropdownPosition && (() => {
                const row = coupons.find((c) => c.id === openMenuId);
                if (!row) return null;
                return createPortal(
                    <div
                        ref={menuRef}
                        className={styles.actionsDropdown}
                        role="menu"
                        style={{
                            position: 'fixed',
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            zIndex: 9999,
                            minWidth: 180,
                        }}
                    >
                        <button
                            type="button"
                            className={styles.actionsDropdownItem}
                            role="menuitem"
                            onClick={() => { handlePrint(row); setOpenMenuId(null); }}
                            disabled={row.status !== 'active'}
                        >
                            <Printer size={14} />
                            <span>Print</span>
                        </button>
                        <button
                            type="button"
                            className={styles.actionsDropdownItem}
                            role="menuitem"
                            onClick={() => { handleExportPdf(row); setOpenMenuId(null); }}
                            disabled={row.status !== 'active'}
                        >
                            <FileDown size={14} />
                            <span>Export PDF</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.actionsDropdownItem} ${styles.actionsDropdownItemDanger}`}
                            role="menuitem"
                            onClick={() => { openDeleteModal(row); setOpenMenuId(null); }}
                            disabled={row.status !== 'active' || deletingId !== null}
                        >
                            {deletingId === row.id ? <Loader2 size={14} className={styles.spin} /> : <Trash2 size={14} />}
                            <span>{deletingId === row.id ? '…' : 'Delete'}</span>
                        </button>
                    </div>,
                    document.body
                );
            })()}

            <Modal
                isOpen={showGenerate}
                onClose={closeGenerate}
                title={createdCoupon ? 'Coupon created' : 'Generate coupon'}
                size="md"
            >
                {createdCoupon ? (
                    <div className={styles.createdWrap}>
                        <p className={styles.createdCode}>
                            Code: <strong>{createdCoupon.code}</strong>
                        </p>
                        <p className={styles.createdMeta}>
                            <span
                                className={styles.planBadge}
                                style={
                                    (createdCoupon.plan || '').toLowerCase() in PLAN_COLORS
                                        ? (() => {
                                            const c = PLAN_COLORS[(createdCoupon.plan || '').toLowerCase()];
                                            return { background: c.bg, color: c.text, border: `1px solid ${c.border}` };
                                        })()
                                        : {}
                                }
                            >
                                {createdCoupon.plan}
                            </span>
                            {' '}• {createdCoupon.amount} •{' '}
                            {createdCoupon.value_type === 'coins'
                                ? `${createdCoupon.coin_value} coins`
                                : createdCoupon.subscription_days
                                    ? `${createdCoupon.subscription_days} days`
                                    : `Till ${formatDate(createdCoupon.subscription_end_date)}`}
                        </p>
                        <div className={styles.createdActions}>
                            <Button variant="primary" onClick={() => handlePrint(createdCoupon)} icon={<Printer size={16} />} className={styles.pillCta}>
                                Print coupon
                            </Button>
                            <Button variant="secondary" onClick={() => handleExportPdf(createdCoupon)} icon={<FileDown size={16} />}>
                                Export PDF
                            </Button>
                            <Button variant="secondary" onClick={closeGenerate}>
                                Done
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={styles.form}>
                            <label className={styles.label}>Plan</label>
                            <select
                                className={styles.select}
                                value={plan}
                                onChange={(e) => setPlan(e.target.value)}
                                aria-label="Plan"
                            >
                                {PLANS.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>

                            <label className={styles.label}>Amount (display on coupon)</label>
                            <Input
                                type="text"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="e.g. ₹500 or $10"
                                aria-label="Amount"
                            />

                            {plan === 'platinum' ? (
                                <>
                                    <label className={styles.label}>Number of days</label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={subscriptionDays}
                                        onChange={(e) => setSubscriptionDays(e.target.value)}
                                        placeholder="e.g. 30, 90"
                                        aria-label="Subscription days"
                                    />
                                </>
                            ) : (
                                <>
                                    <label className={styles.label}>Number of coins</label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={coinValue}
                                        onChange={(e) => setCoinValue(e.target.value)}
                                        placeholder="e.g. 100"
                                        aria-label="Coin value"
                                    />
                                </>
                            )}

                            {createError && (
                                <p className={styles.formError} role="alert">
                                    {createError}
                                </p>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <Button variant="secondary" onClick={closeGenerate}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleCreate}
                                disabled={creating}
                                icon={creating ? <Loader2 size={16} className={styles.spin} /> : null}
                                className={styles.pillCta}
                            >
                                {creating ? 'Creating…' : 'Generate'}
                            </Button>
                        </div>
                    </>
                )}
            </Modal>

            <Modal
                isOpen={!!deleteModal}
                onClose={closeDeleteModal}
                title="Delete coupon?"
                size="sm"
            >
                {deleteModal && (
                    <>
                        <p className={styles.deleteModalBody}>
                            Delete coupon <strong>{deleteModal.code}</strong> ({deleteModal.plan}, {deleteModal.amount})? This cannot be undone. Only active (unredeemed) coupons can be deleted.
                        </p>
                        {deleteError && <p className={styles.formError} role="alert">{deleteError}</p>}
                        <div className={styles.modalFooter}>
                            <Button variant="secondary" onClick={closeDeleteModal}>
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDeleteCoupon}
                                disabled={deletingId !== null}
                                icon={deletingId !== null ? <Loader2 size={16} className={styles.spin} /> : <Trash2 size={16} />}
                            >
                                {deletingId !== null ? 'Deleting…' : 'Delete'}
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default CouponPage;
